import axios from 'axios';
import swal from 'sweetalert';

import services from '../services/auth.service';

const BASE_URL = process.env.REACT_APP_API_ENDPOINT_URL;

export const authttp = axios.create({
    baseURL: BASE_URL,
});

authttp.interceptors.request.use((config) => {
    if (config.url !== '/api/login_token/') {
        const token = localStorage.getItem('_tk_');

        if (token) {
            config.data = { ...config.data, token };
        }
    }

    return config;
});

export const http = axios.create({
    baseURL: BASE_URL,
});

http.interceptors.request.use((config) => {
    const token = localStorage.getItem('_tk_');

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

function hasErrorResponseTypeBlob(error) {
    return (
        error.request.responseType === 'blob' &&
        error.response.data instanceof Blob &&
        error.response.data.type &&
        error.response.data.type.toLowerCase().indexOf('json') !== -1
    );
}

http.interceptors.response.use(
    (response) => response,
    (err) => {
        if (hasErrorResponseTypeBlob(err)) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    err.response.data = JSON.parse(reader.result);
                    resolve(Promise.reject(err));
                };

                reader.onerror = () => {
                    reject(err);
                };

                reader.readAsText(err.response.data);
            });
        }

        if (err.response) {
            switch (err.response.status) {
                case 401: {
                    return on401(err);
                }
                case 403: {
                    const { detail } = err.response.data;

                    if (detail) {
                        swal({
                            title: 'Warning',
                            text: detail,
                            icon: 'warning',
                            button: false,
                        });
                    }

                    break;
                }
                default: {
                    throw err;
                }
            }
        } else {
            swal({
                title: 'Warning',
                text: 'There were problems with the connection.',
                icon: 'warning',
                button: false,
            });
            // store.dispatch(auth.actions.logOut());
        }
    }
);

/**
 * Axios response-error handler for 401 Unauthorized.
 *
 * Attempts a transparent token refresh so the original request can be retried:
 * 1. If there is no refresh token in localStorage the session is already
 *    expired/logged-out – reject immediately without entering a redirect loop.
 * 2. On a successful refresh, update the stored tokens and replay the original
 *    request with the new access token.
 * 3. If the refresh itself fails, clear all client-side session state. The
 *    backend is responsible for redirecting the user to `/login`.
 *
 * @param {import('axios').AxiosError} err - The original 401 error.
 * @returns {Promise} Resolves with the retried response or rejects with the original error.
 */
const on401 = (err) => {
    // no refresh token means we're already logged out, don't retry/redirect again
    if (!localStorage.getItem('_rf_')) {
        return Promise.reject(err);
    }

    return services
        .tokenRefresh()
        .then((response) => {
            const { token, refresh_token } = response.data;
            localStorage.setItem('_tk_', token);
            localStorage.setItem('_rf_', refresh_token);
            err.response.config.headers.Authorization = `Bearer ${token}`;
            return http.request(err.response.config);
        })
        .catch(() => {
            // backend now performs the redirect to /login on auth failure; just clear local session state
            localStorage.removeItem('user');
            localStorage.removeItem('_tk_');
            localStorage.removeItem('_rf_');
            localStorage.removeItem('persist:root');
        });
};

export const zero = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
});

zero.interceptors.request.use((config) => {
    if (config.url !== '/api/login_token/') {
        const token = localStorage.getItem('_tk_');

        if (token) {
            config.data = { ...config.data, token };
        }
    }

    return config;
});
